/**
 * /akun — Profil dan Preferensi entry (Phase 5.2-5.4).
 *
 * Tab ke-3 bottom nav. Renders the Akun tab landing page:
 *  - Greeting + child name
 *  - "Buka Profil dan Preferensi" CTA (PIN-gated)
 *  - "Tentang Aplikasi" link
 *  - App version
 *
 * Phase 5.2 scope: just the landing page + the PIN setup/verify flow.
 * Phase 5.3: Profil dan Preferensi home (Edit Profil, Edit Preferensi, dll).
 * Phase 5.4: Settings + Hapus Data + A11y polish.
 *
 * The Profil dan Preferensi button navigates to /akun/pin which renders the
 * appropriate flow (setup if no PIN, verify if PIN exists).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MainPageHeader } from '@/components/main/MainPageHeader';
import { UserAvatar } from '@/components/main/UserAvatar';
import { getActiveChild } from '@/lib/api/children';
import { logout } from '@/lib/api/auth';
import { resetDailyRecommendations } from '@/lib/api/daily';
import { ApiError, IS_REMOTE_DEV } from '@/lib/api/client';
import { getStoredAccount, isGuestSession } from '@/lib/auth';
import { tierForSession, type SessionTier } from '@/lib/premium';
import { refreshPremiumStatus } from '@/lib/premium-status';
import { APP_VERSION } from '@funpaper/shared';

import type { ChildProfile } from '@funpaper/shared';

type AkunState =
  | { kind: 'loading' }
  | { kind: 'ready'; child: ChildProfile | null }
  | { kind: 'error'; message: string };

export default function AkunScreen() {
  const router = useRouter();
  const [state, setState] = useState<AkunState>({ kind: 'loading' });
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const account = getStoredAccount();
  const isGuest = isGuestSession();
  // Fase 5 plan 23: status premium untuk avatar — refresh dari server saat
  // mount (Akun belum memanggil wallet sebelumnya).
  const [isPremium, setIsPremium] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void refreshPremiumStatus().then((v) => {
      if (!cancelled) setIsPremium(v);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const activeChildId = state.kind === 'ready' ? state.child?.id ?? null : null;

  const onLogout = useCallback(async () => {
    // Two-tap confirm: tap pertama minta konfirmasi, tap kedua eksekusi.
    if (!confirmingLogout) {
      setConfirmingLogout(true);
      return;
    }
    setLoggingOut(true);
    await logout();
    // Guard di root layout akan menangkap sesi kosong, tapi kita replace
    // eksplisit supaya tidak ada flicker konten Profil dan Preferensi.
    router.replace('/login' as never);
  }, [confirmingLogout, router]);

  const onResetRecommendations = useCallback(async () => {
    if (activeChildId === null || resetting) return;
    if (!confirmingReset) {
      setConfirmingReset(true);
      setResetError(null);
      return;
    }

    setResetting(true);
    setResetError(null);
    try {
      await resetDailyRecommendations(activeChildId);
      setConfirmingReset(false);
      router.replace({ pathname: '/home', params: { refresh: String(Date.now()) } });
    } catch (err) {
      setConfirmingReset(false);
      setResetError(
        err instanceof ApiError ? err.message : 'Gagal mereset rekomendasi. Coba lagi.',
      );
    } finally {
      setResetting(false);
    }
  }, [activeChildId, confirmingReset, resetting, router]);

  const loadChild = useCallback(async () => {
    try {
      const child = await getActiveChild();
      setState({ kind: 'ready', child });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Gagal memuat profil anak.';
      setState({ kind: 'error', message });
    }
  }, []);

  useEffect(() => {
    void loadChild();
  }, [loadChild]);

  if (state.kind === 'loading') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingEmoji}>⏳</Text>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  if (state.kind === 'error') {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.errorContainer}>
            <ThemedText type="subtitle" style={styles.errorTitle}>Gagal memuat</ThemedText>
            <ThemedText type="default" themeColor="textSecondary" style={styles.errorBody}>
              {state.message}
            </ThemedText>
            <Pressable onPress={() => void loadChild()} style={styles.retryBtn}>
              <Text style={styles.retryText}>Coba lagi</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  const { child } = state;
  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <AkunHeader
            childName={child?.name ?? null}
            accountName={account?.display_name ?? null}
            accountEmail={isGuest ? null : account?.email ?? null}
            isGuest={isGuest}
            tier={tierForSession({ isGuest, isPremium })}
          />

          {/* Mode Tamu (plan 17): trial belum di-claim — ajak login untuk
              simpan progress. */}
          {isGuest ? (
            <Pressable
              onPress={() => router.replace('/login' as never)}
              style={({ pressed }) => [styles.guestCard, pressed && styles.cardPressed]}
              accessibilityRole="button"
              accessibilityLabel="Masuk untuk Simpan Progress"
            >
              <View style={styles.guestBadge}>
                <Text style={styles.guestBadgeText}>Mode Tamu</Text>
              </View>
              <View style={styles.guestContent}>
                <ThemedText type="smallBold" style={styles.guestTitle}>
                  Masuk untuk Simpan Progress
                </ThemedText>
                <ThemedText type="small" themeColor="textSecondary">
                  Progress dan aktivitas si kecil aman tersimpan di semua perangkat
                </ThemedText>
              </View>
              <Text style={styles.menuChevron}>›</Text>
            </Pressable>
          ) : null}

          {/* Profil dan Preferensi CTA — main feature */}
          <Pressable
            onPress={() => router.push('/akun/pin' as never)}
            style={({ pressed }) => [styles.parentZoneCard, pressed && styles.cardPressed]}
            accessibilityRole="button"
            accessibilityLabel="Buka Profil dan Preferensi"
          >
            <View style={styles.parentZoneContent}>
              <ThemedText type="smallBold" style={styles.parentZoneTitle}>
                Profil dan Preferensi
              </ThemedText>
              <ThemedText type="default" themeColor="textSecondary" style={styles.parentZoneSubtitle}>
                {isGuest
                  ? 'Jelajahi profil dan preferensi tanpa PIN'
                  : 'Kelola profil anak, preferensi, pengaturan, dan data'}
              </ThemedText>
            </View>
            <Text style={styles.parentZoneChevron}>›</Text>
          </Pressable>

          {/* About */}
          <Pressable
            onPress={() => router.push('/akun/tentang' as never)}
            style={({ pressed }) => [styles.menuItem, pressed && styles.cardPressed]}
            accessibilityRole="button"
            accessibilityLabel="Tentang Aplikasi"
          >
            <View style={styles.menuContent}>
              <ThemedText type="smallBold" style={styles.menuTitle}>
                Tentang Aplikasi
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary">
                Versi, kebijakan privasi, kontak
              </ThemedText>
            </View>
            <Text style={styles.menuChevron}>›</Text>
          </Pressable>

          {IS_REMOTE_DEV ? (
            <View>
              <Pressable
                onPress={() => void onResetRecommendations()}
                disabled={resetting || activeChildId === null}
                style={({ pressed }) => [
                  styles.menuItem,
                  styles.remoteDevMenuItem,
                  pressed && styles.cardPressed,
                  (resetting || activeChildId === null) && styles.disabledItem,
                ]}
                accessibilityRole="button"
                accessibilityLabel={
                  resetting
                    ? 'Sedang mereset rekomendasi'
                    : confirmingReset
                    ? 'Yakin? Ketuk lagi untuk reset'
                    : 'Reset rekomendasi (testing)'
                }
              >
                <View style={styles.menuContent}>
                  <ThemedText type="smallBold" style={styles.remoteDevTitle}>
                    {resetting
                      ? 'Mereset rekomendasi…'
                      : confirmingReset
                      ? 'Yakin? Ketuk lagi untuk reset'
                      : 'Reset rekomendasi (testing)'}
                  </ThemedText>
                  <ThemedText type="small" themeColor="textSecondary">
                    Hapus pilihan hari ini agar bisa menguji dari awal
                  </ThemedText>
                </View>
                <Text style={styles.menuChevron}>↻</Text>
              </Pressable>
              {resetError ? <Text style={styles.resetError}>{resetError}</Text> : null}
            </View>
          ) : null}

          {/* Guest tidak perlu logout — sesi guest adalah mode eksplorasi. */}
          {!isGuest ? (
            <Pressable
              onPress={() => void onLogout()}
              disabled={loggingOut}
              style={({ pressed }) => [styles.menuItem, pressed && styles.cardPressed]}
              accessibilityRole="button"
              accessibilityLabel="Keluar dari Akun"
            >
              <View style={styles.menuContent}>
                <ThemedText type="smallBold" style={styles.logoutTitle}>
                  {loggingOut
                    ? 'Sedang keluar…'
                    : confirmingLogout
                    ? 'Yakin? Ketuk lagi untuk keluar'
                    : 'Keluar dari Akun'}
                </ThemedText>
                <ThemedText type="small" themeColor="textSecondary">
                  {account?.email ?? 'Sesi login saat ini'}
                </ThemedText>
              </View>
              <Text style={styles.menuChevron}>›</Text>
            </Pressable>
          ) : null}

          {/* App version footer */}
          <View style={styles.versionFooter}>
            <Text style={styles.versionText}>Funpaper v{APP_VERSION}</Text>
            <Text style={styles.versionSubtext}>
              {child ? `untuk ${child.name} · ${child.age} tahun` : 'Belum ada profil anak'}
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </ThemedView>
  );
}

// ----------------------------------------------------------------------------
// AkunHeader
// ----------------------------------------------------------------------------

function AkunHeader({
  childName,
  accountName,
  accountEmail,
  isGuest,
  tier,
}: {
  childName: string | null;
  accountName: string | null;
  accountEmail: string | null;
  isGuest: boolean;
  tier: SessionTier;
}) {
  const displayName = accountName ?? (isGuest ? 'Teman Funpaper' : 'Orang Tua');
  return (
    <MainPageHeader
      title={`Halo, ${displayName}!`}
      subtitle={accountEmail ?? (isGuest
        ? 'Kamu sedang mencoba aplikasi sebagai tamu'
        : childName
          ? `Selamat mengelola akun ${childName}`
          : 'Selesaikan onboarding dulu untuk mulai')}
      right={(
        <UserAvatar initial={(childName ?? accountName ?? 'F').charAt(0)} tier={tier} size={44} />
      )}
    />
  );
}

// ----------------------------------------------------------------------------
// Styles
// ----------------------------------------------------------------------------

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  scrollContent: {
    padding: Spacing.four,
    paddingBottom: Spacing.five,
    gap: Spacing.three,
  },
  parentZoneCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Brand.primary,
    borderRadius: Radii.medium,
    padding: Spacing.four,
    gap: Spacing.three,
  },
  cardPressed: { opacity: 0.85 },
  parentZoneContent: { flex: 1 },
  parentZoneTitle: { color: '#ffffff', fontWeight: '700' },
  parentZoneSubtitle: { color: 'rgba(255, 255, 255, 0.85)', fontSize: 12, marginTop: 2 },
  parentZoneChevron: { fontSize: 28, color: '#ffffff', fontWeight: '300' },
  guestCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: Brand.primary,
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.three,
  },
  guestBadge: {
    backgroundColor: 'rgba(86, 63, 122, 0.10)',
    borderRadius: Radii.pill,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  guestBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: Brand.primary,
  },
  guestContent: { flex: 1 },
  guestTitle: { color: Brand.primary },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.three,
  },
  menuContent: { flex: 1 },
  menuTitle: { color: '#1A0B5C' },
  remoteDevMenuItem: {
    borderColor: Brand.warning,
    backgroundColor: '#fff7ed',
  },
  remoteDevTitle: { color: '#c2410c' },
  disabledItem: { opacity: 0.55 },
  resetError: {
    color: '#b3261e',
    fontSize: 12,
    marginTop: Spacing.one,
    paddingHorizontal: Spacing.three,
  },
  logoutTitle: { color: '#b3261e' },
  menuChevron: { fontSize: 24, color: '#9CA3AF', fontWeight: '300' },
  versionFooter: {
    alignItems: 'center',
    marginTop: Spacing.four,
    paddingTop: Spacing.four,
  },
  versionText: { fontSize: 12, color: '#9CA3AF', fontWeight: '600' },
  versionSubtext: { fontSize: 11, color: '#9CA3AF', marginTop: 2 },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingEmoji: { fontSize: 48 },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
    gap: Spacing.three,
  },
  errorTitle: { color: Brand.primary, textAlign: 'center' },
  errorBody: { textAlign: 'center', maxWidth: 320 },
  retryBtn: {
    marginTop: Spacing.two,
    paddingHorizontal: Spacing.four,
    paddingVertical: Spacing.two,
    backgroundColor: Brand.primary,
    borderRadius: Radii.medium,
  },
  retryText: { color: '#ffffff', fontSize: 14, fontWeight: '600' },
});
